import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '../../../components/common/Typography';
import Button from '../../../components/common/Button';
import FileInput from '../../../components/common/FileInput';
import { APIBlobRequest } from '../../../common/Common';

const PreviewStep = ({
  imageList,
  onGoToForm,
  onGoToFormDirect,
  onFileSelect,
  isFileChanged = false,
  isEditMode = false,
}) => {
  const { t } = useTranslation('previousEmployment');
  const fileRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [imageBlobUrl, setImageBlobUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const totalPages = imageList.length;

  // 페이지 이미지 로드
  const loadPageImage = async page => {
    if (imageList.length === 0) return;

    setIsLoading(true);
    try {
      const currentFileInfo = imageList[page - 1];

      const params = {
        htmFileType: 'nopaper_ocr',
        htmItemType: '8',
        page: currentFileInfo?.pageNum || page || '1',
        htmDisplayMode: 'inline',
        htmTempUpNm: currentFileInfo?.htmTempUpNm || '',
      };

      if (currentFileInfo?.htmSeq) {
        params.htmSeq = currentFileInfo.htmSeq.trim();
      }

      const response = await APIBlobRequest('/YA/download', params);

      if (response) {
        // 기존 blob URL 해제
        if (imageBlobUrl) {
          window.URL.revokeObjectURL(imageBlobUrl);
        }

        // 새 blob URL 생성
        const blobUrl = window.URL.createObjectURL(response);
        setImageBlobUrl(blobUrl);
      }
    } catch (error) {
      console.error('이미지 로드 에러:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 페이지 변경 시 이미지 로드
  useEffect(() => {
    if (imageList.length > 0) {
      loadPageImage(currentPage);
    }

    return () => {
      if (imageBlobUrl) {
        window.URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, [currentPage, imageList]);

  // 컴포넌트 언마운트 시 blob URL 정리
  useEffect(() => {
    return () => {
      if (imageBlobUrl) {
        window.URL.revokeObjectURL(imageBlobUrl);
      }
    };
  }, []);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // 파일 변경 핸들러
  const handleFileChange = files => {
    if (files && files.length > 0) {
      // 여러 파일 선택 시 모든 파일 전달
      onFileSelect(Array.from(files));
    }
  };

  // 다시 올리기 버튼 클릭 시 파일 선택 다이얼로그 열기
  const handleRetryClick = () => {
    fileRef.current.click();
  };

  return (
    <div className="displayFlexColumn" style={{ gap: '16px', height: '100%' }}>
      {/* 파일 변경 경고 배너 */}
      {isFileChanged && (
        <div
          style={{
            background: 'var(--Warning-50, #FFF8E6)',
            border: '1px solid var(--Warning-200, #FFD666)',
            borderRadius: '8px',
            padding: '12px 16px',
          }}>
          <Typography variant="body2Medium" style={{ color: 'var(--Warning-700, #A66D00)' }}>
            {/* 새 파일이 업로드되었습니다. 데이터 확인 후 저장해야 반영됩니다. */}
            {t('previewSection.fileChangedWarning')}
          </Typography>
        </div>
      )}

      {/* 페이지 정보 및 다시 올리기 */}
      <div
        className="displayFlex"
        style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2Medium">
          {/* n / n 페이지 */}
          {t('previewSection.pageInfo', { current: currentPage, total: totalPages })}
        </Typography>
        <Button
          variant="contained"
          color="secondary"
          size="xSmall"
          onClick={handleRetryClick}
          // 다시 올리기
          value={t('previewSection.retryButton')}
        />
      </div>

      {/* 이미지 미리보기 */}
      <div
        style={{
          flex: 1,
          overflow: 'auto',
          background: 'var(--GrayScale-100)',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-start',
          padding: '16px',
        }}>
        {isLoading ? (
          <Typography variant="body2Medium" style={{ color: 'var(--GrayScale-600)' }}>
            {/* 이미지 로딩 중... */}
            {t('previewSection.imageLoading')}
          </Typography>
        ) : imageBlobUrl ? (
          <img
            src={imageBlobUrl}
            // 페이지 n
            alt={t('previewSection.pageAlt', { page: currentPage })}
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        ) : (
          <Typography variant="body2Medium" style={{ color: 'var(--GrayScale-600)' }}>
            {/* 이미지를 불러올 수 없습니다. */}
            {t('previewSection.imageLoadError')}
          </Typography>
        )}
      </div>

      {/* 페이지 네비게이션 */}
      {totalPages > 1 && (
        <div className="displayFlex" style={{ justifyContent: 'center', gap: '16px' }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            // 이전
            value={t('previewSection.prevButton')}
          />
          <Button
            variant="contained"
            color="secondary"
            size="small"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            // 다음
            value={t('previewSection.nextButton')}
          />
        </div>
      )}

      {/* 데이터 확인 및 저장 버튼 */}
      {/* 수정모드에서 파일 변경 없으면: 데이터 확인하기 -> 바로 폼으로 */}
      {/* 신규 등록 또는 파일 변경시: 파일 저장하고 데이터 입력하기 -> AI 선택으로 */}
      {isEditMode && !isFileChanged ? (
        // 데이터 확인하기
        <Button
          color="primary"
          onClick={onGoToFormDirect}
          value={t('previewSection.checkDataButton')}
        />
      ) : (
        // 파일 저장하고 데이터 입력하기
        <Button color="primary" onClick={onGoToForm} value={t('previewSection.goToFormButton')} />
      )}

      {/* 숨겨진 파일 입력 */}
      <FileInput
        fileRef={fileRef}
        fileHandler={e => handleFileChange(e.target.files)}
        accept=".pdf,.jpg,.gif,.png,.jpeg"
        multiple
      />
    </div>
  );
};

export default PreviewStep;
